import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'

import type { MenuItem } from '../domain/menu-item'

export type CategoryLoadState = 'index' | 'loading' | 'loaded' | 'error'

interface CategoryItemsState {
  fullItemsByCategory: Record<string, MenuItem[]>
  loadStateByCategory: Record<string, CategoryLoadState>
  errorsByCategory: Record<string, string | null>
}

interface CategoryItemsActions {
  setFullItems: (categoryId: string, items: MenuItem[]) => void
  setCategoryLoadState: (categoryId: string, state: CategoryLoadState) => void
  setCategoryError: (categoryId: string, error: string | null) => void
  getFullItemsForCategory: (categoryId: string) => MenuItem[] | undefined
  getLoadStateForCategory: (categoryId: string) => CategoryLoadState
  needsFullItemsLoad: (categoryId: string) => boolean
  markCategoryAsIndexOnly: (categoryId: string) => void
  reset: () => void
}

type CategoryItemsStore = CategoryItemsState & CategoryItemsActions

const initialState: CategoryItemsState = {
  fullItemsByCategory: {},
  loadStateByCategory: {},
  errorsByCategory: {},
}

export const useCategoryItemsStore = create<CategoryItemsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setFullItems: (categoryId, items) =>
        set(
          (state) => ({
            fullItemsByCategory: {
              ...state.fullItemsByCategory,
              [categoryId]: items,
            },
            loadStateByCategory: {
              ...state.loadStateByCategory,
              [categoryId]: 'loaded',
            },
            errorsByCategory: {
              ...state.errorsByCategory,
              [categoryId]: null,
            },
          }),
          false,
          'setFullItems',
        ),

      setCategoryLoadState: (categoryId, loadState) =>
        set(
          (state) => ({
            loadStateByCategory: {
              ...state.loadStateByCategory,
              [categoryId]: loadState,
            },
          }),
          false,
          'setCategoryLoadState',
        ),

      setCategoryError: (categoryId, error) =>
        set(
          (state) => ({
            errorsByCategory: {
              ...state.errorsByCategory,
              [categoryId]: error,
            },
            loadStateByCategory: {
              ...state.loadStateByCategory,
              [categoryId]: 'error',
            },
          }),
          false,
          'setCategoryError',
        ),

      getFullItemsForCategory: (categoryId) => {
        const state = get()
        return state.fullItemsByCategory[categoryId]
      },

      getLoadStateForCategory: (categoryId) => {
        const state = get()
        return state.loadStateByCategory[categoryId] ?? 'loaded'
      },

      needsFullItemsLoad: (categoryId) => {
        const state = get()
        const loadState = state.loadStateByCategory[categoryId]
        return loadState === 'index'
      },

      markCategoryAsIndexOnly: (categoryId) =>
        set(
          (state) => ({
            loadStateByCategory: {
              ...state.loadStateByCategory,
              [categoryId]: 'index',
            },
          }),
          false,
          'markCategoryAsIndexOnly',
        ),

      reset: () => set(initialState, false, 'reset'),
    }),
    {
      name: 'category-items-store',
    },
  ),
)

export const useCategoryLoadState = (categoryId: string) =>
  useCategoryItemsStore((state) => state.loadStateByCategory[categoryId] ?? 'loaded')

export const useCategoryFullItems = (categoryId: string) =>
  useCategoryItemsStore((state) => state.fullItemsByCategory[categoryId])

export const useCategoryError = (categoryId: string) =>
  useCategoryItemsStore((state) => state.errorsByCategory[categoryId])

export const useCategoryItemsActions = () =>
  useCategoryItemsStore(
    useShallow((state) => ({
      setFullItems: state.setFullItems,
      setCategoryLoadState: state.setCategoryLoadState,
      setCategoryError: state.setCategoryError,
      markCategoryAsIndexOnly: state.markCategoryAsIndexOnly,
      reset: state.reset,
    })),
  )
